import { useEffect, useState } from "react";
import { api, formatBRL } from "../../lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

export default function Relatorios() {
  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState([]);
  // RC19.2 — utilização mensal (contratado vs utilizado)
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [util, setUtil] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/reports/kpis").then(r => setKpis(r.data)),
      api.get("/reports/revenue-series").then(r => setSeries(r.data)),
    ]).catch(() => {});
  }, []);

  useEffect(() => {
    if (!month) return;
    api.get(`/reports/utilization?month=${month}`).then(r => setUtil(r.data)).catch(() => setUtil(null));
  }, [month]);

  if (!kpis) return <div className="text-gray-500">Carregando…</div>;

  const pieData = [
    { name: "Pagas", value: kpis.paid_invoices, fill: "#10B981" },
    { name: "Pendentes", value: Math.max(0, kpis.total_invoices - kpis.paid_invoices - kpis.overdue_invoices), fill: "#F59E0B" },
    { name: "Vencidas", value: kpis.overdue_invoices, fill: "#EF4444" },
  ];

  return (
    <div data-testid="admin-relatorios">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Relatórios</div>
        <h1 className="font-display text-3xl text-[#14213D] font-light">Indicadores & KPIs</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "MRR", value: formatBRL(kpis.mrr), color: "#D4AF37" },
          { label: "Receita do mês", value: formatBRL(kpis.revenue_month), color: "#14213D" },
          { label: "Clientes ativos", value: kpis.active_clients, color: "#10B981" },
          { label: "Conversão de leads", value: `${kpis.conversion_rate}%`, color: "#3B82F6" },
        ].map((c) => (
          <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{c.label}</div>
            <div className="font-display text-2xl" style={{ color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="font-display text-xl text-[#14213D] mb-4">Receita histórica (6 meses)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series}>
              <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#9CA3AF" fontSize={11} />
              <YAxis stroke="#9CA3AF" fontSize={11} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatBRL(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#14213D" strokeWidth={3} dot={{ fill: "#D4AF37", r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="font-display text-xl text-[#14213D] mb-4">Distribuição de faturas</h3>
          {kpis.total_invoices === 0 ? (
            <div className="h-[300px] flex flex-col items-center justify-center text-gray-400">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3 text-gray-300 text-2xl">∅</div>
              <div className="text-sm">Sem faturas registradas ainda</div>
              <div className="text-xs mt-1">Crie faturas em Financeiro para ver os dados aqui.</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} label>
                  {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* RC19.2 — Utilização mensal: contratado vs utilizado */}
      <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6" data-testid="utilization-report">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h3 className="font-display text-xl text-[#14213D]">Utilização mensal — contratado vs utilizado</h3>
            <p className="text-xs text-gray-500 mt-0.5">Reservas com check-out no mês selecionado (horário de São Paulo)</p>
          </div>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-[#14213D]"
            data-testid="utilization-month-input"
          />
        </div>

        {!util || util.totals.reservations === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center" data-testid="utilization-empty">
            Nenhum check-out registrado neste mês.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="utilization-totals">
              {[
                ["Check-outs", util.totals.reservations],
                ["Contratado", fmtH(util.totals.contracted_hours)],
                ["Utilizado", fmtH(util.totals.used_hours)],
                ["Banco de Horas", fmtH(util.totals.bank_hours_consumed)],
                ["Excedente", `${fmtH(util.totals.overage_hours)} · ${formatBRL(util.totals.overage_amount)}`],
              ].map(([label, value]) => (
                <div key={label} className="bg-gray-50 rounded-lg px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{label}</div>
                  <div className="text-sm font-bold text-[#14213D] mt-0.5 truncate">{value}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <UtilTable title="Por cliente" rows={util.by_client} testid="utilization-by-client" />
              <UtilTable title="Por espaço" rows={util.by_space} testid="utilization-by-space" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const fmtH = (h) => `${Number(h || 0).toFixed(1).replace(".", ",").replace(",0", "")}h`;

function UtilTable({ title, rows, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold mb-2">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[420px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <th className="py-2 pr-3">Nome</th>
              <th className="py-2 pr-3 text-right">Res.</th>
              <th className="py-2 pr-3 text-right">Contratado</th>
              <th className="py-2 pr-3 text-right">Utilizado</th>
              <th className="py-2 text-right">Excedente</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-gray-50 last:border-0" data-testid={`${testid}-row-${r.id}`}>
                <td className="py-2 pr-3 font-semibold text-[#14213D] truncate max-w-[180px]">{r.name}</td>
                <td className="py-2 pr-3 text-right text-gray-600">{r.reservations}</td>
                <td className="py-2 pr-3 text-right text-gray-600">{fmtH(r.contracted_hours)}</td>
                <td className={`py-2 pr-3 text-right font-bold ${r.used_hours > r.contracted_hours ? "text-amber-600" : "text-[#14213D]"}`}>{fmtH(r.used_hours)}</td>
                <td className="py-2 text-right">
                  {r.overage_hours > 0
                    ? <span className="text-amber-600 font-semibold">{fmtH(r.overage_hours)} · {formatBRL(r.overage_amount)}</span>
                    : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
